// @file Composable to enable dropping files/links/text to create a post.
import { __ } from '@@/bits/intl'
import { useExpandedPostStore } from '@@/pinia/expanded_post'
import { usePostComposerModalStore } from '@@/pinia/post_composer_modal_store'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfaceDragAndDropEventsStore } from '@@/pinia/surface_drag_and_drop_events_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { isInnermostDropTarget } from '@@/pragmatic-drag-and-drop/helpers'
import { useContentDrop } from '@@/vuecomposables/useContentDrop'
import { useCanHandleDropFromExternal } from '@@/vuecomposables/useDragAndDropFromExternalMonitor'
import { dropTargetForExternal, type ExternalEventPayloadMap } from '@atlaskit/pragmatic-drag-and-drop/external/adapter'
import { containsFiles, getFiles } from '@atlaskit/pragmatic-drag-and-drop/external/file'
import { containsText, getText } from '@atlaskit/pragmatic-drag-and-drop/external/text'
import { containsURLs, getURLs } from '@atlaskit/pragmatic-drag-and-drop/external/url'
import type { CleanupFn } from '@atlaskit/pragmatic-drag-and-drop/types'
import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, onMounted, ref, type Ref } from 'vue'

interface PostPosition {
  top: number
  left: number
}

enum DraggedDataType {
  File,
  Link,
  Text,
}

/**
 * Handles drop events to create a post.
 */
const useDropToCreatePost = ({
  postComposerModalElement,
  onFileDropWithExistingAttachment,
}: {
  /**
   * By default, this composable handles drop events on the whole document.
   * If you pass the post composer modal element here, the dropped content
   * will be added to that composer.
   */
  postComposerModalElement?: Ref<Element | undefined>
  /**
   * Optional callback executed *before* showing the default alert
   * when a file is dropped while the active draft already has an attachment.
   * If this function is provided and returns `false`, the default alert and
   * replacement logic will be skipped. If it returns `true` or is not provided,
   * the default logic proceeds.
   * @param file The file that was dropped.
   * @returns {boolean} Whether to proceed with the default alert/logic.
   */
  onFileDropWithExistingAttachment?: (file: File) => boolean
} = {}): void => {
  const { stopHandlingDragEvent, setDropActionMessage } = useSurfaceDragAndDropEventsStore()
  const { handleFilesDrop, handleLinkDrop, handleTextDrop } = useContentDrop({
    onFileDropWithExistingAttachment,
  })

  const { xEditThumbnailModal } = storeToRefs(usePostComposerModalStore())
  const { isAnyDraftActive } = storeToRefs(useSurfaceDraftsStore())

  /*****************
   * DRAGGING STATES
   *****************/

  const isDroppingIntoPostComposer = computed(() => postComposerModalElement?.value != null)
  const draggedDataType = ref<DraggedDataType | null>(null)

  const setDraggedDataType = (e: ExternalEventPayloadMap['onDragStart']): void => {
    if (containsFiles(e)) {
      draggedDataType.value = DraggedDataType.File
    } else if (containsURLs(e)) {
      draggedDataType.value = DraggedDataType.Link
    } else if (containsText(e)) {
      draggedDataType.value = DraggedDataType.Text
    }
  }

  const dropActionMessage = computed<string | null>(() => {
    if (draggedDataType.value === DraggedDataType.File) {
      if (isDroppingIntoPostComposer.value) return __('Drop the file here to add it to this post')
      return isAnyDraftActive.value
        ? __('Drop the file anywhere to add it to this post')
        : __('Drop files anywhere to create a new post')
    }
    if (draggedDataType.value === DraggedDataType.Link) {
      if (isDroppingIntoPostComposer.value) return __('Drop the link here to add it to this post')
      return isAnyDraftActive.value
        ? __('Drop the link anywhere to add it to this post')
        : __('Drop links anywhere to create a new post')
    }
    if (draggedDataType.value === DraggedDataType.Text) {
      if (isDroppingIntoPostComposer.value) return __('Drop the text here to add it to this post')
      return isAnyDraftActive.value
        ? __('Drop the text anywhere to add it to this post')
        : __('Drop text anywhere to create a new post')
    }
    return null
  })

  /***************
   * DROP HANDLERS
   ***************/

  /**
   * Returns post top and left position (the post appears at the drop position in Canvas).
   */
  const getDropPosition = (e: ExternalEventPayloadMap['onDrop']): PostPosition => {
    const { pageX, pageY } = e.location.current.input
    const wishListElement = document.getElementById('wish-list')
    const left = Math.max(pageX - (wishListElement != null ? wishListElement.getBoundingClientRect().left : 0), 0)
    const top = Math.max(pageY - (wishListElement != null ? wishListElement.getBoundingClientRect().top : 0), 0)
    return { top, left }
  }

  /************
   * LIFE CYCLE
   ************/

  let cleanUp: CleanupFn

  const { canHandle } = useCanHandleDropFromExternal()
  const { canIPost } = storeToRefs(useSurfacePermissionsStore())
  const { shouldHandleDragAndDropToPost } = storeToRefs(useSurfacePostPropertiesStore())
  const { xExpandedPostPanel } = storeToRefs(useExpandedPostStore())

  onMounted(() => {
    const element = isDroppingIntoPostComposer.value ? postComposerModalElement?.value : document.body
    if (element == null) return
    cleanUp = dropTargetForExternal({
      element,
      canDrop: (e) => {
        return (
          !xEditThumbnailModal.value && // If edit thumbnail modal is open skip handling drop for other cases
          canHandle(e) &&
          canIPost.value && // User must have permission.
          shouldHandleDragAndDropToPost.value && // Attachments must be enabled.
          !xExpandedPostPanel.value // Disable in expanded view.
        )
      },
      onDragEnter: (e) => {
        setDraggedDataType(e)
        setDropActionMessage(dropActionMessage.value)
      },
      onDropTargetChange: () => {
        setDropActionMessage(dropActionMessage.value)
      },
      onDragLeave: () => {
        // When handling drop events in the post composer modal,
        // don't stop if the drag leaves the composer as we can
        // handle it to create a new post instead of adding to
        // the active composer.
        if (!isDroppingIntoPostComposer.value) {
          stopHandlingDragEvent()
          setDropActionMessage(null)
        }
      },
      onDrop: (e) => {
        // We have 2 drop targets in play:
        // 1. The active post composer modal (for adding to the active draft)
        // 2. `document.body` (for creating a new draft)
        // When we drop something into the composer, both `onDrop` of the 2 targets are executed.
        // (documentation: https://atlassian.design/components/pragmatic-drag-and-drop/core-package/drop-targets#nested-drop-targets)
        // We only want to handle the innermost drop target to prevent double handling.
        // We do this by checking if the target element is the same as the listened element.
        if (!isInnermostDropTarget(e)) {
          return
        }

        const position = getDropPosition(e)

        if (draggedDataType.value === DraggedDataType.File) {
          void handleFilesDrop({ files: getFiles(e), position })
        } else if (draggedDataType.value === DraggedDataType.Link) {
          handleLinkDrop({ urls: getURLs(e), position })
        } else if (draggedDataType.value === DraggedDataType.Text) {
          handleTextDrop({ text: getText(e), position })
        }
      },
    })
  })

  onBeforeUnmount(() => {
    cleanUp?.()
  })
}

export { useDropToCreatePost }
